import { Box, LinearProgress, Typography } from "@mui/material"
import { UploadFileOutlined } from "@mui-symbols-material/w300"
import type { ReactElement } from "react"
import { useCallback, useMemo, useRef } from "react"
import type { Control, FieldArrayPath, FieldValues } from "react-hook-form"
import { useFieldArray } from "react-hook-form"

import { MultiFileUploadRow } from "./MultiFileUploadRow"
import type { UploadRowStatus } from "./types"
import { getFieldFile, getOverLimitMessage, getRejectionsMessage, partitionFilesForUpload } from "./utils"

import {
  BYTES_PER_MB,
  DOCUMENT_UPLOAD_MIME_TYPES,
  DOCUMENT_UPLOAD_TYPES_LABEL,
  MAX_FILE_NAME_LENGTH,
  MAX_UPLOAD_FILES,
  MAX_UPLOAD_SIZE_BYTES,
} from "@/components/upload/constants"
import { convertMimeTypesToAccept } from "@/components/upload/utils"
import { useMultiFileDrop } from "@/hooks/useMultiFileDrop"
import type { UploadRowProgress } from "@/hooks/useMultiFileUpload"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"
import type { Option } from "@/types/form-fields"

interface MultiFileUploadProps {
  control: Control<any>
  name: string
  testIdPrefix: string
  documentTypeOptions?: Option<string>[]
  withDescription?: boolean
  existingFileNames?: string[]
  allowedMimeTypes?: readonly string[]
  allowedTypesLabel?: string
  statuses?: Record<string, UploadRowStatus>
  progress?: Record<string, UploadRowProgress>
  isUploading?: boolean
  readOnly?: boolean
}

export const MultiFileUpload = ({
  control,
  name,
  testIdPrefix,
  documentTypeOptions,
  withDescription = false,
  existingFileNames = [],
  allowedMimeTypes = DOCUMENT_UPLOAD_MIME_TYPES,
  allowedTypesLabel = DOCUMENT_UPLOAD_TYPES_LABEL,
  statuses,
  progress,
  isUploading = false,
  readOnly = false,
}: MultiFileUploadProps): ReactElement => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const inputRef = useRef<HTMLInputElement>(null)

  const { fields, append, remove } = useFieldArray({
    control: control as Control<FieldValues>,
    name: name as FieldArrayPath<FieldValues>,
  })

  const accept = useMemo(() => convertMimeTypesToAccept([...allowedMimeTypes]), [allowedMimeTypes])
  const maxSizeMb = Math.round(MAX_UPLOAD_SIZE_BYTES / BYTES_PER_MB)

  const uploadedCount = useMemo(
    () =>
      statuses ? Object.values(statuses).filter((status) => status === "uploaded" || status === "failed").length : 0,
    [statuses]
  )

  const handleFilesAdded = useCallback(
    (incomingFiles: File[]) => {
      if (readOnly || isUploading) return

      const pendingFiles = fields.map((field) => getFieldFile(field))
      const { accepted, rejections, overLimit } = partitionFilesForUpload({
        incomingFiles,
        pendingFiles,
        existingFileNames,
        maxFiles: MAX_UPLOAD_FILES,
        maxSizeBytes: MAX_UPLOAD_SIZE_BYTES,
        maxFileNameLength: MAX_FILE_NAME_LENGTH,
        allowedMimeTypes: [...allowedMimeTypes],
      })

      if (overLimit) {
        notify({ type: NotificationType.Error, message: getOverLimitMessage(overLimit) })
        return
      }

      const rejectionsMessage = getRejectionsMessage(rejections, {
        maxSizeBytes: MAX_UPLOAD_SIZE_BYTES,
        maxFileNameLength: MAX_FILE_NAME_LENGTH,
      })
      if (rejectionsMessage) {
        notify({ type: NotificationType.Error, message: rejectionsMessage })
      }

      if (!accepted.length) return
      append(accepted.map((file) => ({ file, documentType: "", description: "" })))
    },
    [readOnly, isUploading, fields, existingFileNames, allowedMimeTypes, notify, append]
  )

  const isDropZoneDisabled = readOnly || isUploading || fields.length >= MAX_UPLOAD_FILES

  const { dropRef, isOver } = useMultiFileDrop({ onDrop: handleFilesAdded, enabled: !isDropZoneDisabled })

  const handleClick = useCallback(() => {
    if (isDropZoneDisabled) return
    inputRef.current?.click()
  }, [isDropZoneDisabled])

  const handleDropZoneKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault()
        handleClick()
      }
    },
    [handleClick]
  )

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? [])
      if (files.length) handleFilesAdded(files)
      event.target.value = ""
    },
    [handleFilesAdded]
  )

  return (
    <Box>
      <input
        ref={inputRef}
        type='file'
        multiple
        accept={accept}
        onChange={handleInputChange}
        style={{ display: "none" }}
        data-testid={`${testIdPrefix}.file-input`}
      />
      {!readOnly && (
        <Box
          ref={dropRef}
          role='button'
          tabIndex={isDropZoneDisabled ? -1 : 0}
          aria-disabled={isDropZoneDisabled}
          onClick={handleClick}
          onKeyDown={handleDropZoneKeyDown}
          className={`mb-4 flex w-full flex-col items-center justify-center gap-2 rounded border border-dashed px-4 py-6 outline-none transition-colors focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
            isDropZoneDisabled
              ? "cursor-not-allowed border-gray-300 bg-gray-100 opacity-60"
              : `cursor-pointer ${isOver ? "border-primary bg-blue-50" : "border-gray-400 hover:border-primary hover:bg-gray-50"}`
          }`}
          data-testid={`${testIdPrefix}.drop-zone`}
        >
          <UploadFileOutlined className='text-primary' />
          <Typography variant='subtitle1' className='text-center font-normal text-gray-900'>
            <Box component='span' className='text-primary underline'>
              Click to upload
            </Box>{" "}
            or drag and drop multiple files
          </Typography>
          <Typography variant='body2' color='textSecondary' className='text-center'>
            {allowedTypesLabel} (max. {maxSizeMb}MB, up to {MAX_UPLOAD_FILES} files)
          </Typography>
        </Box>
      )}
      {isUploading && statuses && (
        <Box className='mb-4'>
          <Typography variant='caption' className='text-gray-600'>
            Uploading {uploadedCount} of {fields.length}
          </Typography>
          <LinearProgress variant='determinate' value={fields.length ? (uploadedCount / fields.length) * 100 : 0} />
        </Box>
      )}
      {!isUploading && fields.length > 0 && (
        <Typography
          variant='caption'
          className='mb-2 block text-gray-600'
          data-testid={`${testIdPrefix}.selected-count`}
        >
          {fields.length} of {MAX_UPLOAD_FILES} files selected
        </Typography>
      )}
      <Box className='flex max-h-[360px] flex-col gap-3 overflow-y-auto'>
        {fields.map((field, index) => (
          <MultiFileUploadRow
            key={field.id}
            control={control}
            name={name}
            index={index}
            file={getFieldFile(field)}
            documentTypeOptions={documentTypeOptions}
            withDescription={withDescription}
            status={statuses?.[index]}
            progress={progress?.[index]}
            isUploading={isUploading}
            onRemove={() => remove(index)}
            testIdPrefix={testIdPrefix}
          />
        ))}
      </Box>
    </Box>
  )
}
